// K.A.N.R.I. Method module — API layer.
const API = process.env.REACT_APP_BACKEND_URL;

const token = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

const call = async (path, options = {}) => {
  const res = await fetch(`${API}/api${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = 'Request failed';
    try { detail = (await res.json()).detail || detail; } catch (_) { /* keep default */ }
    throw new Error(detail);
  }
  return res.json();
};

export const getMethodContent = () => call('/method/content');
export const getMethodDepartments = () => call('/method/departments');
export const getBrochure = () => call('/method/brochure');
export const getMetrics = (from, to) => call(`/method/operating-metrics?date_from=${from}&date_to=${to}`);

export const getMgmtAudits = (from, to) => call(`/method/mgmt-audits?date_from=${from}&date_to=${to}`);
export const saveMgmtAudit = (body) => call('/method/mgmt-audits', { method: 'PUT', body: JSON.stringify(body) });

export const getMeasures = () => call('/method/measures');
export const saveMeasure = (body) => call('/method/measures', { method: 'PUT', body: JSON.stringify(body) });

export const getLaunch = () => call('/method/launch');
export const saveLaunch = (body) => call('/method/launch', { method: 'PUT', body: JSON.stringify(body) });

export const getFailureReviews = () => call('/method/failure-reviews');
export const createFailureReview = (body) =>
  call('/method/failure-reviews', { method: 'POST', body: JSON.stringify(body) });

export const downloadMaturityPdf = async (id = 'latest') =>
  pdf(`/method/maturity/${id}/export.pdf`,
    id === 'blank' ? 'kanri-maturity-check-blank.pdf' : 'kanri-maturity-check.pdf');
export const downloadPocketGuidePdf = async () => pdf('/method/pocket-guide/export.pdf', 'kanri-pocket-guide.pdf');
export const downloadBrochurePdf = async ({ recipient = '', recipientOrg = '', purpose = '', nda = false,
  personal = false } = {}) => {
  const qs = new URLSearchParams({ recipient, recipient_org: recipientOrg, purpose, nda: String(nda),
    personal: String(personal) });
  const headers = await pdf(`/method/brochure/export.pdf?${qs}`, 'KANRI Method Brochure.pdf');
  if (nda && !personal) {
    const copyNo = headers?.get('x-copy-number') || '';
    const nqs = new URLSearchParams({ copy_no: copyNo, recipient, recipient_org: recipientOrg });
    await pdf(`/method/brochure/undertaking.pdf?${nqs}`, 'kanri-undertaking.pdf');
  }
};
export const getBrochureIssues = () => call('/method/brochure/issues');
export const setBrochureIssueStatus = (id, status, note = '') =>
  call(`/method/brochure/issues/${id}`, { method: 'PATCH', body: JSON.stringify({ status, note }) });

export const boardPdfUrl = (deptId, date) => `/method/board/export.pdf?department_id=${deptId}&date=${date}`;
export const downloadBoardPdf = async (deptId, date, deptName = 'board') =>
  pdf(boardPdfUrl(deptId, date), `kanri-board-${deptName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${date}.pdf`);

const pdf = async (path, filename) => {
  const res = await fetch(`${API}/api${path}`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!res.ok) {
    let detail = 'Export failed';
    try { detail = (await res.json()).detail || detail; } catch (_) { /* keep default */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const served = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') || '');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = served ? served[1] : filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return res.headers;
};

export const getToday = (date, departmentId = '') =>
  call(`/method/today?date=${date}${departmentId ? `&department_id=${departmentId}` : ''}`);

export const getConditions = (from, to) => call(`/method/conditions?date_from=${from}&date_to=${to}`);
export const saveCondition = (body) => call('/method/conditions', { method: 'PUT', body: JSON.stringify(body) });

export const getAlignments = (from, to) => call(`/method/alignments?date_from=${from}&date_to=${to}`);
export const saveAlignment = (body) => call('/method/alignments', { method: 'PUT', body: JSON.stringify(body) });

export const getNavigations = (from, to) => call(`/method/navigations?date_from=${from}&date_to=${to}`);
export const createNavigation = (body) => call('/method/navigations', { method: 'POST', body: JSON.stringify(body) });

export const getResponses = (status = 'all') => call(`/method/responses?status=${status}`);
export const createResponse = (body) => call('/method/responses', { method: 'POST', body: JSON.stringify(body) });
export const patchResponse = (id, body) => call(`/method/responses/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const getStandards = (status = 'all') => call(`/method/standards?status=${status}`);
export const createStandard = (body) => call('/method/standards', { method: 'POST', body: JSON.stringify(body) });
export const verifyStandard = (id, body) => call(`/method/standards/${id}/verify`, { method: 'POST', body: JSON.stringify(body) });

export const getRhythm = (date, departmentId = '') =>
  call(`/method/rhythm?date=${date}${departmentId ? `&department_id=${departmentId}` : ''}`);
export const setRhythm = (body) => call('/method/rhythm', { method: 'PUT', body: JSON.stringify(body) });

export const getAdoption = () => call('/method/adoption');
export const setAdoption = (body) => call('/method/adoption', { method: 'PUT', body: JSON.stringify(body) });

export const getMaturity = () => call('/method/maturity');
export const submitMaturity = (body) => call('/method/maturity', { method: 'POST', body: JSON.stringify(body) });

export const getBureaucracyReviews = () => call('/method/bureaucracy-reviews');
export const createBureaucracyReview = (body) =>
  call('/method/bureaucracy-reviews', { method: 'POST', body: JSON.stringify(body) });

export const LEVEL_TONE = {
  green: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
  amber: 'bg-amber-500/15 text-amber-700 border-amber-500/40',
  red: 'bg-rose-500/15 text-rose-700 border-rose-500/40',
  black: 'bg-foreground/10 text-foreground border-foreground/40',
};
